import axios, { AxiosInstance } from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import store from "@/store";
import { SET_SESSION_TOKEN, SET_REFRESH_TOKEN } from "@/store/mutation-types";
import { AXIOS_TIMEOUT } from "@/utils/const";
import { REFRESH_TOKEN_URL } from "@/utils/urls";
import baseAPI from "@/api/base";

// Deliberately a bare axios instance with no interceptors registered — the
// normal `baseAPI` instance's response interceptor is what calls into this
// module in the first place, so reusing it here would recurse back through
// itself the moment the refresh call also fails.
//
// Built lazily (not at module load) because this module sits in an import
// cycle with base.ts (base -> responseInterceptor -> errorHandler ->
// classifiedErrorHandler -> tokenRefresh -> base). Reading baseAPI.defaults
// at the top level would run while base.ts is still mid-initialization,
// before it has assigned/exported baseAPI, and crash with "Cannot read
// properties of undefined (reading 'defaults')".
let refreshClient: AxiosInstance | null = null;
function getRefreshClient(): AxiosInstance {
  if (!refreshClient) {
    refreshClient = axios.create({
      baseURL: baseAPI.defaults.baseURL,
      headers: { "Content-Type": "application/json" },
      timeout: AXIOS_TIMEOUT,
      withCredentials: true,
    });
  }
  return refreshClient;
}

export interface RefreshOutcome {
  token: string | null;
  // True when the refresh endpoint couldn't be reached at all (offline,
  // timeout). Distinct from the endpoint being reached and rejecting the
  // refresh token — callers should not treat a connectivity blip the same
  // as a genuinely invalid session.
  networkError: boolean;
}

async function performRefresh(): Promise<RefreshOutcome> {
  const currentRefreshToken = store.getters.refreshToken;
  if (!currentRefreshToken) return { token: null, networkError: false };

  try {
    const response = await getRefreshClient().post(REFRESH_TOKEN_URL, {
      refreshToken: currentRefreshToken,
    });

    // The user may have logged out (or another login/refresh may have
    // replaced the token) while this request was in flight. Committing
    // tokens now would re-persist a session the user already ended.
    // Single-flight means only one refresh runs at a time, so any change
    // here can only have come from outside this function.
    if (store.getters.refreshToken !== currentRefreshToken) {
      return { token: null, networkError: false };
    }

    // The backend rotates the refresh token on every use, so the token we
    // just sent is already spent server-side regardless of what comes
    // back. A response missing the new refreshToken can't be treated as
    // success — there'd be nothing valid left to refresh with next time.
    if (response?.data?.status && response.data.sessionToken && response.data.refreshToken) {
      store.commit(SET_SESSION_TOKEN, response.data.sessionToken);
      store.commit(SET_REFRESH_TOKEN, response.data.refreshToken);
      return { token: response.data.sessionToken, networkError: false };
    }
    return { token: null, networkError: false };
  } catch (err) {
    const isNetworkError = axios.isAxiosError(err) && !err.response;
    return { token: null, networkError: isNetworkError };
  }
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

// Single-flight: if several requests 401 at the same moment (the normal
// case — the token just expired), only one /auth/refresh call fires and
// everyone else awaits the same promise. The backend rotates the refresh
// token on every use and treats reuse of an already-spent one as a
// compromise signal that revokes the whole session — so firing two
// concurrent refreshes would log the user out, not just waste a call.
export function getRefreshedAccessToken(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export function isRefreshEndpoint(url?: string): boolean {
  return !!url && url.includes(REFRESH_TOKEN_URL);
}

export async function retryWithToken(config: InternalAxiosRequestConfig, newToken: string) {
  config.headers.Authorization = `Bearer ${newToken}`;
  return baseAPI(config);
}
